import type { AnimationEventHandler } from 'react';
import React from 'react';
import { Badge } from 'antd';
import { GiftOutlined, InboxOutlined } from '@ant-design/icons';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import Tooltip from 'components/common/tooltip';
import { IconButton } from 'components/common/icon-button';
import { useSessionStorageState, useUpdateEffect } from 'ahooks';
import { EventNames, track } from 'utils/analytics.utils';

interface Props {
  isRead: boolean;
  onClick: () => void;
}

/**
 * Shared by both designs, so the keyframes live outside either branch's styled component. The
 * animation sits on the wrapper rather than the control, so it rotates the whole affordance
 * including the unread dot.
 */
const wiggle = css`
  &.wiggle {
    animation: 1s wiggle ease;
    animation-iteration-count: 5;
  }

  @keyframes wiggle {
    0% {
      transform: rotate(-3deg);
    }
    20% {
      transform: rotate(20deg);
    }
    40% {
      transform: rotate(-15deg);
    }
    60% {
      transform: rotate(5deg);
    }
    90% {
      transform: rotate(-1deg);
    }
    100% {
      transform: rotate(0);
    }
  }
`;

export const AppNotificationsButton: React.FC<Props> = ({ isRead, onClick }) => {
  const { isNewDesign } = useTheme();
  const [shouldWiggle, setShouldWiggle] = useSessionStorageState('should-app-notifications-button-wiggle', {
    defaultValue: true
  });

  useUpdateEffect(() => {
    if (!isRead) {
      setShouldWiggle(true);
    }
  }, [isRead, setShouldWiggle]);

  const onAnimationEnd: AnimationEventHandler<HTMLElement> = e => {
    if (e.target === e.currentTarget) {
      setShouldWiggle(false);
    }
  };

  const onAnimationStart: AnimationEventHandler<HTMLElement> = e => {
    if (e.target === e.currentTarget) {
      track(EventNames.IN_APP_WHATS_NEW_SHOW_NEW_ITEMS);
    }
  };

  const wiggleClass = shouldWiggle && !isRead ? 'wiggle' : '';

  /*
   * Forked rather than shared: `IconButton`'s legacy branch is a bare span with its own offsets, which
   * would change the switch-off render (D7.7).
   *
   * `whats-new-button` and the click handler stay on the wrapper, because that is what animates and a
   * click on the button inside bubbles up to it.
   */
  if (isNewDesign) {
    return (
      <WiggleBox
        data-e2e="whats-new-button"
        className={wiggleClass}
        onClick={onClick}
        onAnimationStart={onAnimationStart}
        onAnimationEnd={onAnimationEnd}>
        <Badge dot={!isRead} offset={[-6, 6]}>
          <IconButton
            tooltipTitleId="whats.new"
            dataE2e="whats-new-icon"
            ariaLabel="What's new"
            icon={InboxOutlined}
            size="small"
          />
        </Badge>
      </WiggleBox>
    );
  }

  return (
    <Tooltip placement="left" titleId="whats.new">
      <Wrapper
        color="black"
        onClick={onClick}
        className={wiggleClass}
        data-e2e="whats-new-button"
        onAnimationStart={onAnimationStart}
        onAnimationEnd={onAnimationEnd}>
        <Badge dot={!isRead}>
          <GiftOutlined className="gift" />
        </Badge>
      </Wrapper>
    </Tooltip>
  );
};

// New-design only, so it needs no branch. Sizing comes from the cluster's icon-button rule in
// topbar-view, which this and the theme toggle now share.
const WiggleBox = styled.span`
  display: inline-flex;
  align-items: center;

  ${wiggle}
`;

// Switch-off only, and unchanged from before the restyle — which is what keeps the parity fixture green.
const Wrapper = styled.div`
  display: grid;
  place-items: center;

  ${wiggle}

  .gift {
    font-size: 24px;
  }

  &:hover {
    cursor: pointer;
  }
`;
